import React, { useCallback, useMemo, useRef } from 'react';
import t from 'prop-types';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';

/**
 * Reactstrap Dropdowns break when you don't have any DropdownItem elements
 * inside them and try to open them with the keyboard. And when you do have
 * DropdownItems and a form, the form never gets focus in tab order because the
 * Dropdown hijacks keyboard events.
 *
 * To get around this and support having a form inside a dropdown, this
 * component adds an invisible DropdownItem and uses its focus event to pass
 * focus to the first form element.
 */

const generateFormDropdownToggle =
  ({ formDropdownToggleRef }) =>
  ({ children, ...props }) => {
    return (
      <DropdownToggle {...props} innerRef={formDropdownToggleRef}>
        {children}
      </DropdownToggle>
    );
  };

const generateFormDropdownMenu =
  ({ formDropdownToggleRef, toggle }) =>
  ({ children, ...props }) => {
    // Handler for passing focus from invisible DropdownItem to the form
    const handleFocus = e => {
      // Determine if user shift tabbed from the menu back to the DropdownItem
      const shouldLoseFocus =
        e.currentTarget.parentElement.contains(e.relatedTarget) &&
        // Don't lose focus if previous element has hasAutoFocus class set. This
        // is a workaround for when you have an autofocused input inside the
        // dropdown form to ensure that it doesn't immediately close when opened
        // via keyboard. THis breaks closing the dropdown by shift-tab if the
        // autofocused element is the first focusable element in the form.
        !e.relatedTarget.classList.contains('hasAutoFocus');

      // If user shift tabbed out of the menu, close the dropdown and focus
      // the toggle component.
      if (shouldLoseFocus) {
        toggle();
        formDropdownToggleRef.current && formDropdownToggleRef.current.focus();
      }
      // Otherwise, find all form and button elements inside the menu, and
      // select the first non-disabled one to focus.
      else {
        const formEls = e.target.parentElement.querySelectorAll(
          '.form-control, .form-select, .form-check-input, .btn',
        );
        const focusableEls = Array.from(formEls).filter(
          el => !el.disabled && el.focus,
        );
        // Focus the correct element
        if (focusableEls.length > 0) {
          // If the first element is a radio button find the active one to focus
          const toFocus =
            focusableEls[0].type === 'radio'
              ? focusableEls.find(
                  el =>
                    el.type === 'radio' &&
                    el.name === focusableEls[0].name &&
                    el.checked,
                ) || focusableEls[0]
              : focusableEls[0];
          toFocus.focus();
        }
        // If there isn't a valid element to focus, just blur the DropdownItem
        else {
          e.target.blur();
        }
      }
    };

    // Handler to stop propagation of keyDown events within the menu to prevent
    // the Dropdown component from disabling default form behavior, like using
    // arrow keys to select options from a select element.
    const handleKeyDown = e => {
      e.stopPropagation();
    };

    // Handler to stop propagation of Escape keyUp events within the menu to
    // prevent an Escape keyUp from closing parent containers.
    const handleKeyUp = e => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        // On escape, close the dropdown
        toggle();
        formDropdownToggleRef.current && formDropdownToggleRef.current.focus();
      }
    };

    return (
      <DropdownMenu {...props} onKeyDown={handleKeyDown} onKeyUp={handleKeyUp}>
        <DropdownItem
          onFocus={handleFocus}
          tag="span"
          className="dropdown-item__form-trigger"
        />
        {children}
      </DropdownMenu>
    );
  };

export const FormDropdown = ({ toggle, children, ...props }) => {
  const formDropdownToggleRef = useRef();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const FormDropdownToggle = useMemo(
    () =>
      generateFormDropdownToggle({
        formDropdownToggleRef,
      }),
    [],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const FormDropdownMenu = useMemo(
    () =>
      generateFormDropdownMenu({
        formDropdownToggleRef,
        toggle,
      }),
    [toggle],
  );

  return (
    <Dropdown {...props} toggle={toggle}>
      {typeof children === 'function'
        ? children({
            FormDropdownToggle,
            FormDropdownMenu,
          })
        : children}
    </Dropdown>
  );
};

FormDropdown.propTypes = {
  // Render function that is passed an object with `FormDropdownToggle` and
  // `FormDropdownMenu` properties, which are components that should be rendered
  // inside the Dropdown.
  children: t.func,
};
